import React from 'react';
import { withInject, errorBoundary } from '@piwikpro/platform';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import {
  Button,
  HelpLink,
  Text,
  Stack,
  withTranslation,
  Information,
  StyledContainer,
  AutoSavedCheckboxControl,
  Icon,
  Card,
  ContactForm,
} from '@piwikpro/ui-components';
import { AuthSettings, AuthState } from '@piwikpro/auth-crate';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { Notifications } from '@piwikpro/notification-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { OrganizationState } from '@piwikpro/organization-crate';

const mapStateToProps = (state: {
  session: SessionState
  organization: OrganizationState
  authSettings: AuthState['authSettings']
  notifications: Notifications
}) => ({
  isSamlEnabled: state.authSettings.organization.saml.enabled,
  isSamlAvailable: state.organization.details.license?.current?.plan?.administration?.sso_available ?? false,
  isSamlVerified: state.authSettings.organization.saml.verified,
  isAutoProvisioningEnabled: state.authSettings.organization.autoProvisioning.enabled,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  productAnalytics: ProductAnalytics
  authSettings: AuthSettings
  notifications: Notifications
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

export const SamlDescription: React.FC<Props> = ({
  isSamlEnabled,
  isSamlAvailable,
  authSettings,
  isSamlVerified,
  isAutoProvisioningEnabled,
  productAnalytics,
  notifications,
  t,
}) => {
  const InactiveState = () => (
    <StyledContainer
      backgroundImage="image3"
      backgroundColor="white"
      border={['bottom', 'left', 'right', 'top']}
      padding="wide"
    >
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item>
          <Icon size="large" name="shield" color="blue" />
        </Stack.Item>
        <Stack.Item>
          <Text as="p" weight={600} dataId="Label">{t('accountSecurity.saml-settings.inactive.title')}</Text>
        </Stack.Item>
        <Stack.Item previousSpacing="narrow">
          <Text as="p">
            {t('accountSecurity.saml-settings.inactive.description-part-1-new')}
          </Text>
          <Text as="p" align="center">
            {t('accountSecurity.saml-settings.inactive.description-part-2-new')}
          </Text>
        </Stack.Item>
        <Stack.Item>
          <Stack>
            <Stack.Item>
              <ContactForm
                onSubmitSuccess={({ message }) => {
                  notifications.push({
                    text: message,
                    type: 'success',
                    autoCloseSeconds: 5,
                  });
                }}
                onSubmitFail={({ message }) => {
                  notifications.push({
                    text: message,
                    type: 'error',
                    autoCloseSeconds: 5,
                  });
                }}
                type="account"
              >
                {({ open }) => (
                  <Button
                    onClick={() => {
                      open();
                      productAnalytics.push({ type: '@platform/action/auth/samlSetup/contactUsButtonClick' });
                    }}
                    text={t('platform.license.contact-us.button')}
                  />
                )}
              </ContactForm>
            </Stack.Item>
            <Stack.Item>
              <Button
                onClick={() => window.open('https://help.piwik.pro/support/account/saml-single-sign-on/', '_blank')}
                text={t('components:helpLinkReadMore')}
                appearance="simple"
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </StyledContainer>
  );

  const handleSubmitCheckbox = async (values, { setFieldValue, setSubmitting }) => {
    try {
      await authSettings.setAutoProvisioningOfUsers({ enabled: values.autoProvisioning });
      await authSettings.getSettingsForOrganization();
    } catch (err) {
      setFieldValue(
        'autoProvisioning',
        !values.autoProvisioning,
        false,
      );
    }

    setSubmitting(false);
  };

  if (!isSamlAvailable) {
    return <InactiveState />;
  }

  return (
    <Card.Section>
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item fill>
          <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('accountSecurity.saml-settings.title')}</Text>
        </Stack.Item>
        <Stack.Item fill previousSpacing="narrow">
          <Text as="p">
            {t('accountSecurity.saml-settings.description')}
          </Text>
        </Stack.Item>
        <Stack.Item fill>
          <Information type="info">
            {t('accountSecurity.saml-settings.enterprise.saml-available')}
          </Information>
        </Stack.Item>
        {isSamlVerified && isSamlEnabled && (
          <Stack.Item fill>
            <AutoSavedCheckboxControl
              onSubmit={handleSubmitCheckbox}
              initialValue={isAutoProvisioningEnabled}
              label={t('accountSecurity.saml-settings.auto-provisioning-checkbox.label')}
              name="autoProvisioning"
            />
          </Stack.Item>
        )}
        <Stack.Item>
          <HelpLink href="https://help.piwik.pro/support/account/saml-single-sign-on/">
            Set up SAML single sign-on
          </HelpLink>
        </Stack.Item>
      </Stack>
    </Card.Section>
  );
};

export default connector(withInject<InjectedProps>({
  productAnalytics: 'productAnalytics',
  authSettings: 'AuthCrate.authSettings',
  notifications: 'NotificationCrate.notifications',
})(withTranslation(['administration'])(errorBoundary(SamlDescription))));
